import React, { useContext, useMemo } from "react";
import LanguageContext from "../helper/LanguageContext";
import MaterialIcon from "./MaterialIcon";
import { looksLikeHtml } from "../helper/emailBodyFormat";

export function isDailyBriefingEmail(email) {
  if (!email) return false;
  if (email.systemTag === "daily_briefing") return true;
  const from = email.fromAddress || {};
  const fromEmail = String(from.email || from.address || email.from || "").toLowerCase();
  const subject = String(email.subject || "");
  return fromEmail === "info@kjuup.de" && /tages[-\s]?briefing|tages[-\s]?bericht|daily\s*(briefing|report)/i.test(subject);
}

function prepareDocumentHtml(html) {
  let body = String(html || "");
  const styles = [];
  body = body.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, (s) => {
    styles.push(s);
    return "";
  });
  body = body.replace(/<title[^>]*>[\s\S]*?<\/title>/gi, "");
  body = body.replace(/<meta[^>]*\/?>/gi, "");
  body = body.replace(/<link[^>]*\/?>/gi, "");
  body = body.replace(/<\/?(html|head|body)(?:\s[^>]*)?>/gi, "");
  body = body.replace(/<!DOCTYPE[^>]*>/gi, "");
  return { styles: styles.join("\n"), body: body.trim() };
}

function stripCockpitLabel(html) {
  return String(html || "").replace(
    /<div[^>]*>\s*MANAGEMENT[-\s]?COCKPIT\s*<\/div>\s*/gi,
    ""
  );
}

function alignOpenDecisionKpi(html) {
  const src = String(html || "");
  const section = src.match(
    /<h2[^>]*>\s*(?:Offene Entscheidungen|Open decisions)\s*<\/h2>([\s\S]*?)(?=<h2[\s>]|$)/i
  );
  if (!section) return src;
  const body = section[1] || "";
  const visible = body.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  const count = /^(keine eintr[äa]ge\.?|no entries\.?)$/i.test(visible)
    ? 0
    : (body.match(/<strong\b/gi) || []).length;
  return src.replace(
    /(font-size:\s*26px[^>]*>)\s*\d+(\s*<\/td>\s*<\/tr>\s*<tr>\s*<td[^>]*>\s*(?:Entscheidungen|Decisions))/i,
    `$1${count}$2`
  );
}

function resolveDocumentHtml(email) {
  const raw = (email && (email.bodyHtml || email.bodyText || email.body || "")) || "";
  if (!raw) return "";
  if (!looksLikeHtml(raw)) {
    return `<pre style="white-space:pre-wrap;font-family:inherit;margin:0;padding:20px;">${String(raw)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")}</pre>`;
  }
  const prepared = prepareDocumentHtml(raw);
  return `${prepared.styles}${stripCockpitLabel(alignOpenDecisionKpi(prepared.body))}`;
}

function briefingFileName(email) {
  const raw = String((email && email.subject) || "Tagesbericht")
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return `${raw || "Tagesbericht"}.html`;
}

function downloadBriefingHtml(email, html) {
  if (!html) return;
  const title = String((email && email.subject) || "Tagesbericht");
  const documentHtml =
    `<!DOCTYPE html><html lang="de"><head><meta charset="utf-8">` +
    `<meta name="viewport" content="width=device-width, initial-scale=1">` +
    `<title>${title.replace(/</g, "&lt;").replace(/>/g, "&gt;")}</title>` +
    `</head><body>${html}</body></html>`;
  const blob = new Blob([documentHtml], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = briefingFileName(email);
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function DailyBriefingDocument({ email, onClose, onDelete }) {
  const { strings } = useContext(LanguageContext);
  const html = useMemo(() => resolveDocumentHtml(email), [email]);

  return (
    <div className="briefing-document">
      <div className="briefing-document-toolbar">
        {onClose ? (
          <button
            type="button"
            className="kj-btn kj-btn--icon kj-btn--lg kj-btn--neutral kj-btn--round"
            onClick={onClose}
            title={strings.email_back || "Back"}
          >
            <MaterialIcon icon="arrow_back" size={20} />
          </button>
        ) : (
          <span />
        )}
        <div className="briefing-document-toolbar-actions">
          <button
            type="button"
            className="kj-btn kj-btn--icon kj-btn--lg kj-btn--neutral kj-btn--round kj-btn--email"
            title={strings.email_download || "Download"}
            onClick={() => downloadBriefingHtml(email, html)}
            disabled={!html}
          >
            <MaterialIcon icon="download" size={18} />
          </button>
          <button
            type="button"
            className="kj-btn kj-btn--icon kj-btn--lg kj-btn--neutral kj-btn--round kj-btn--danger"
            title={strings.email_delete || "Delete"}
            onClick={() => onDelete && onDelete(email)}
            disabled={!onDelete}
          >
            <MaterialIcon icon="delete" size={18} />
          </button>
        </div>
      </div>
      <div className="briefing-document-scroll">
        {html ? (
          <div className="briefing-document-body" dangerouslySetInnerHTML={{ __html: html }} />
        ) : (
          <div className="email-attachments-empty">{strings.email_loading || "Loading..."}</div>
        )}
      </div>
    </div>
  );
}
